import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

const COLLAPSED_DETAIL_LINES = 2;
const LONG_CHAR_THRESHOLD = 180;

type ExpandableScrollMessageProps = {
  message: string;
  /** Accent for the Read more / Show less control. */
  accentColor?: string;
};

/**
 * Keeps notification dialogs inside the viewport for long multi-line messages
 * (e.g. batch allocate / unassign failures): summary on top, details behind
 * Read more, scrollable when expanded.
 */
const ExpandableScrollMessage: React.FC<ExpandableScrollMessageProps> = ({
  message,
  accentColor = "#f44336",
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [message]);

  const lines = useMemo(() => message.split("\n"), [message]);
  const summary = lines[0] ?? message;
  const detailLines = lines.slice(1);
  const isLong =
    detailLines.length > COLLAPSED_DETAIL_LINES ||
    message.length > LONG_CHAR_THRESHOLD ||
    lines.length > COLLAPSED_DETAIL_LINES + 1;

  const typographySx = {
    fontSize: { xs: "0.9rem", sm: "1rem" },
    color: "#424242",
    lineHeight: 1.7,
    fontWeight: 400,
    maxWidth: "100%",
    wordBreak: "break-word" as const,
    whiteSpace: "pre-wrap" as const,
  };

  if (!isLong) {
    return (
      <Typography
        variant="body1"
        id="error-dialog-description"
        sx={{
          ...typographySx,
          textAlign: lines.length > 1 ? "left" : "center",
        }}
      >
        {message}
      </Typography>
    );
  }

  const collapsedDetails = detailLines.slice(0, COLLAPSED_DETAIL_LINES);
  const visibleDetails = expanded ? detailLines : collapsedDetails;
  const showToggle =
    detailLines.length > COLLAPSED_DETAIL_LINES ||
    (detailLines.length === 0 && message.length > LONG_CHAR_THRESHOLD);

  return (
    <Box sx={{ width: "100%", textAlign: "left" }}>
      <Typography
        variant="body1"
        id="error-dialog-description"
        sx={{
          ...typographySx,
          fontWeight: 600,
          textAlign: "center",
          mb: detailLines.length > 0 || expanded ? 1 : 0,
        }}
      >
        {summary}
      </Typography>

      {(visibleDetails.length > 0 ||
        (expanded && detailLines.length === 0)) && (
        <Box
          sx={{
            maxHeight: expanded ? "45vh" : undefined,
            overflowY: expanded ? "auto" : "visible",
            pr: expanded ? 0.5 : 0,
            textAlign: "left",
            borderRadius: "8px",
            bgcolor: expanded ? "rgba(0,0,0,0.03)" : "transparent",
            px: expanded ? 1 : 0,
            py: expanded ? 1 : 0,
          }}
        >
          <Typography variant="body1" sx={typographySx}>
            {detailLines.length > 0
              ? visibleDetails.join("\n")
              : // Single long paragraph with no newlines — show full text when expanded
                message}
          </Typography>
        </Box>
      )}

      {showToggle && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <Button
            size="small"
            onClick={() => setExpanded((prev) => !prev)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: accentColor,
              minWidth: 0,
              px: 1,
            }}
          >
            {expanded ? "Show less" : "Read more"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ExpandableScrollMessage;
